import { useCallback, useEffect, useState } from 'react';
import { fetchKlachtBehandeling } from '../../lib/klachten';
import { KLACHT_STAP_LABEL, type KlachtBehandeling, type KlachtBeoordeling } from '../../lib/klacht-plan';
import { useInboxStore } from '../../stores/inbox-store';

type Oordeel = Omit<KlachtBeoordeling, 'door' | 'at'>;

/** Bediening van het bestaande beoordeel-primitief, bij het gebonden mailconcept. */
export function InboxWasserijBeoordeling() {
  const plan = useInboxStore(s => s.klachtAdvies?.plan);
  const conversationId = useInboxStore(s => s.activeThread?.conversation.id);
  const messageId = useInboxStore(s => s.aiActiesMessageId);
  if (!plan?.behandeling_id || !conversationId || !messageId) return null;
  return <WasserijBeoordelingKaart key={`${conversationId}:${messageId}:${plan.behandeling_id}`}
    behandelingId={plan.behandeling_id} itemReferentie={plan.item_referentie} />;
}

function WasserijBeoordelingKaart({ behandelingId, itemReferentie }: { behandelingId: string; itemReferentie: string | null }) {
  const [b, setBehandeling] = useState<KlachtBehandeling | null>(null);
  const [enabled, setEnabled] = useState(true);
  const [laden, setLaden] = useState(true);
  const [error, setError] = useState('');
  const [melding, setMelding] = useState('');
  const [bewerken, setBewerken] = useState(false);
  const [opgeslagenZonderConcept, setOpgeslagenZonderConcept] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const save = useInboxStore(s => s.saveKlachtBeoordeling);
  const generate = useInboxStore(s => s.generateKlachtAdvies);
  const busy = useInboxStore(s => s.klachtBeoordelingBezig || s.isGeneratingKlachtAdvies || s.isGeneratingDraft || s.actieBusyIndex !== null || s.isSending);

  useEffect(() => {
    let cancelled = false;
    setLaden(true);
    void fetchKlachtBehandeling(behandelingId).then(data => {
      if (cancelled) return;
      const gevonden = data.behandelingen.find(v => v.id === behandelingId && (!itemReferentie || v.wi_naam === itemReferentie));
      if (!gevonden) throw new Error('De gekoppelde wasserijbeoordeling is niet beschikbaar. Genereer het klachtadvies opnieuw.');
      setBehandeling(gevonden);
      setEnabled(data.enabled);
      setError('');
    }).catch(e => { if (!cancelled) setError((e as Error).message); })
      .finally(() => { if (!cancelled) setLaden(false); });
    return () => { cancelled = true; };
  }, [behandelingId, itemReferentie, refresh]);

  const submit = useCallback(async (oordeel: Oordeel) => {
    if (!b || busy) return;
    setError(''); setMelding('');
    const r = await save(b, oordeel);
    if (r.behandeling) {
      setBehandeling(r.behandeling);
      setBewerken(false);
      setOpgeslagenZonderConcept(!r.conceptGemaakt);
    }
    if (r.error) setError(r.error);
    if (r.conceptGemaakt) setMelding('Het nieuwe antwoord staat hieronder. Klik op Gebruiken om het over te nemen; je eigen antwoordtekst is niet aangepast.');
  }, [b, busy, save]);

  const retryConcept = async () => {
    if (!b || busy) return;
    setError('');
    await generate(undefined, { id: b.id, versie: b.versie });
    const s = useInboxStore.getState();
    if (s.klachtAdvies?.plan?.behandeling_id !== behandelingId) return;
    if (s.klachtAdviesError) setError(s.klachtAdviesError);
    else if (s.klachtAdvies.plan.beoordeling_versie === b?.versie) {
      setOpgeslagenZonderConcept(false);
      setMelding('Het nieuwe antwoord staat hieronder. Controleer het en klik op Gebruiken.');
    }
  };
  const kanBeoordelen = b && ['beoordeling', 'aanbod', 'wacht_klant'].includes(b.stap);

  return <section aria-label="Wasserijbeoordeling bij deze mail" className="my-3 rounded-lg border border-indigo-300 bg-white p-4 space-y-3">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <h2 className="text-sm font-semibold text-slate-900">Wasserijbeoordeling{itemReferentie ? ` · ${itemReferentie}` : ''}</h2>
      <button type="button" disabled={busy || laden} onClick={() => { setBewerken(false); setRefresh(v => v + 1); }} className="text-xs text-indigo-700 disabled:opacity-50">Beoordeling verversen</button>
    </div>
    {laden && <p role="status" className="text-sm text-slate-600">Wasserijbeoordeling ophalen…</p>}
    {b && !laden && <>
      {!b.beoordeling && <p className="text-sm text-slate-700">De beoordeling is aangevraagd. Er is nog geen technisch oordeel vastgelegd. Jij of de wasserij kan het hier toevoegen en meteen een klantantwoord laten maken.</p>}
      {b.beoordeling && <div className="space-y-1 text-sm text-slate-700">
        <p className="font-medium">{b.beoordeling.uitkomst === 'behandelen' ? b.beoordeling.behandeling : 'Geen verdere behandeling'}</p>
        <p>{b.beoordeling.verwachting}</p>
        {b.beoordeling.risicos.length > 0 && <ul className="list-disc pl-5">{b.beoordeling.risicos.map((r, i) => <li key={i}>{r}</li>)}</ul>}
        <p className="whitespace-pre-wrap">{b.beoordeling.toelichting}</p>
        <p className="text-xs text-slate-500">Vastgelegd door {b.beoordeling.door} · versie {b.versie} · {KLACHT_STAP_LABEL[b.stap]}</p>
      </div>}
      {!enabled && <p className="text-sm text-amber-800">De klachtketen staat uit. Je kunt de beoordeling nu alleen lezen.</p>}
      {kanBeoordelen && enabled && !bewerken && <button type="button" disabled={busy} onClick={() => setBewerken(true)} className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50">
        {b.beoordeling ? 'Wasserijbeoordeling aanpassen' : 'Wasserijbeoordeling toevoegen'}
      </button>}
      {bewerken && enabled && <WasserijOordeelFormulier key={`${b.id}:${b.versie}`} beoordeling={b.beoordeling} busy={busy} onSubmit={submit} onCancel={() => setBewerken(false)} />}
      {opgeslagenZonderConcept && !bewerken && <button type="button" disabled={busy} onClick={() => void retryConcept()} className="ml-2 rounded-md border border-indigo-600 px-3 py-2 text-sm text-indigo-700 disabled:opacity-50">Alleen antwoord opnieuw maken</button>}
    </>}
    {busy && <p role="status" className="text-sm text-indigo-700">Bezig met opslaan of het nieuwe antwoord. Dit kan één tot twee minuten duren. Er wordt niets verstuurd.</p>}
    {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
    {melding && !busy && <p role="status" className="text-sm text-emerald-800">{melding}</p>}
  </section>;
}

/** Alleen invoer; opslag, versiecontrole en conceptgeneratie lopen via API/store. */
export function WasserijOordeelFormulier({ beoordeling, busy, onSubmit, onCancel }: {
  beoordeling: KlachtBeoordeling | null; busy: boolean;
  onSubmit: (oordeel: Oordeel) => Promise<void>; onCancel: () => void;
}) {
  const [uitkomst, setUitkomst] = useState<Oordeel['uitkomst'] | ''>(beoordeling?.uitkomst || '');
  const [behandeling, setBehandeling] = useState(beoordeling?.behandeling || '');
  const [verwachting, setVerwachting] = useState(beoordeling?.verwachting || '');
  const [risicos, setRisicos] = useState(beoordeling?.risicos.join('\n') || '');
  const [toelichting, setToelichting] = useState(beoordeling?.toelichting || '');
  const inputClass = 'mt-1 block w-full rounded-md border border-slate-300 bg-white p-2 text-sm';
  return <form className="space-y-3 border-t border-slate-200 pt-3" onSubmit={e => {
    e.preventDefault();
    if (busy || !uitkomst) return;
    void onSubmit({ uitkomst, behandeling: uitkomst === 'behandelen' ? behandeling.trim() : '', verwachting: verwachting.trim(), risicos: risicos.split('\n').map(s => s.trim()).filter(Boolean), toelichting: toelichting.trim() });
  }}>
    <fieldset disabled={busy} className="space-y-3">
      <label className="block text-sm font-medium text-slate-800">Wat is het technische oordeel?
        <select required className={inputClass} value={uitkomst} onChange={e => setUitkomst(e.target.value as typeof uitkomst)}>
          <option value="">Kies de uitkomst</option><option value="behandelen">Een aanvullende behandeling is verantwoord</option><option value="niet_behandelen">Geen zinvolle of verantwoorde aanvullende behandeling</option>
        </select>
      </label>
      {uitkomst === 'behandelen' && <label className="block text-sm font-medium text-slate-800">Welke behandeling is mogelijk?
        <textarea required minLength={5} maxLength={400} rows={2} className={inputClass} value={behandeling} onChange={e => setBehandeling(e.target.value)} />
      </label>}
      <label className="block text-sm font-medium text-slate-800">Verwachte verbetering{uitkomst === 'niet_behandelen' ? ' of reden waarom verder behandelen niet zinvol is' : ''}
        <textarea required minLength={5} maxLength={400} rows={2} className={inputClass} value={verwachting} onChange={e => setVerwachting(e.target.value)} />
      </label>
      <label className="block text-sm font-medium text-slate-800">Specifieke risico’s
        <textarea rows={3} className={inputClass} value={risicos} onChange={e => setRisicos(e.target.value)} placeholder="Eén risico per regel; leeg als er geen nieuwe risico’s zijn" />
      </label>
      <label className="block text-sm font-medium text-slate-800">Toelichting van jou of de wasserij
        <textarea required minLength={5} maxLength={1000} rows={3} className={inputClass} value={toelichting} onChange={e => setToelichting(e.target.value)} placeholder="Wat is vastgesteld en wat moet de schrijver over deze situatie weten?" />
      </label>
      <p className="text-xs text-slate-500">Dit wordt het vastgelegde technische oordeel bij dit kleed. Behandeling, verwachting en risico’s gaan mee in het klantaanbod. Een herziening maakt eerder aanbod en klantakkoord ongeldig. Opslaan start geen behandeling en verstuurt geen mail.</p>
      <div className="flex flex-wrap gap-2">
        <button type="submit" className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50">{busy ? 'Beoordeling opslaan en antwoord maken…' : 'Beoordeling opslaan en antwoord maken'}</button>
        <button type="button" onClick={onCancel} className="rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-700">Annuleren</button>
      </div>
    </fieldset>
  </form>;
}
